import React, { useState } from 'react';
import { format } from 'date-fns';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { Box, Button, Table, Text, TextButton } from '@zillow/constellation-10';
import { parseQueryString } from '@zg-rentals/string-utils';
import {
  AuthCheckWrapper,
  HeadWrapper,
  LoadingConstants,
  numberUtils,
  useRentalManagerUser,
  useRmwPaths,
} from '@zg-rentals/rental-manager-env';
import { PageWrapper } from '@zg-rentals/rental-revenue-components';

// Constants
import BillingError from '../../components/BillingError';
import SpinnerContainer from '../../components/SpinnerContainer';
import PaymentInfoModal from '../../components/modals/PaymentInfoModal';
import BillingPortalMaintenanceBanner from '../../components/BillingPortalMaintenanceBanner';
import billingUtils from '../../utils/billingUtils';
import { withErrorBoundary } from '../../components/withErrorBoundry';
import { useInvoiceDownloader } from '../../hooks/useInvoiceDownloader';
import { downloadRocketshipCsv, downloadSingleFamilyLegacyCsv } from '../../utils/downloadUtils';
import { useInvoice } from '../../hooks/useInvoice';
import { useAppMaintenance } from '../../hooks/useMaintenance';

import { SfhBreakdownTable } from './SfhBreakdownTable';
import { ListingSpotlightTable } from './ListingSpotlightTable';
import { InvoiceItemDetailsTable } from './InvoiceItemDetailsTable';
import { StyledBalanceBox, StyledContainer, StyledSummaryBox } from './StyledComponents';
import { InvoicePaymentDisplay } from '../../components/InvoicePaymentDisplay';
import { type AdproductsZuoraInvoicePaymentDto, ZuoraInvoiceType } from '@zg-rentals/ts-adproducts-client';
import { emitViewRentalsBillingInvoiceEvent, EVENT_IDS } from '../../model/ClickstreamEvents';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';
import { localDateToDate } from '../../utils/dateUtils';

const DEFAULT_PAGE_TITLE = 'Invoice';

const formatDate = (value: string | number) => value && format(new Date(value), 'MM/dd/yyyy');

const InvoicePage = () => {
  const match = useRouteMatch();
  // @ts-ignore
  const invoiceId: string = match.params.invoiceId;
  const { user } = useRentalManagerUser();
  const {
    invoice: currentInvoice,
    errorMessage: currentInvoiceErrorMessage,
    loadingState: currentInvoiceLoadingState,
    fetchInvoice,
  } = useInvoice();

  const { isMaintenanceMode } = useAppMaintenance();
  const location = useLocation();
  const queryParams = parseQueryString(location.search);
  const isSingleFamilyLegacy =
    currentInvoice &&
    (currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_REIT ||
      currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_FLAT_FEE);
  const paymentsEnabled = user.features.canAccessInvoicePayments;
  const paths = useRmwPaths();
  const [showPaymentInfoModal, setShowPaymentInfoModal] = useState(false);
  const { getInvoices } = useInvoiceDownloader();
  const history = useHistory();

  const handleEditClick = (accountNumber: string) => {
    const url = `${paths.billingPage}/account/${accountNumber}`;
    const target = `${url}?prev=invoice&invoiceNumber=${invoiceId}`;
    history.push(target);
  };

  React.useEffect(() => {
    window.scroll(0, 0);
    fetchInvoice(invoiceId);
    emitViewRentalsBillingInvoiceEvent({ user });
  }, []);

  const handlePayInfo = () => setShowPaymentInfoModal(true);

  const renderPaymentHistory = () => {
    if (!currentInvoice || currentInvoice.invoicePayments.length === 0) return null;
    const sortedPayments = currentInvoice.invoicePayments
      .slice()
      .sort(
        (a: AdproductsZuoraInvoicePaymentDto, b: AdproductsZuoraInvoicePaymentDto) => b.createdTime - a.createdTime,
      );
    const columnCount = paymentsEnabled ? 6 : 4;
    return (
      <StyledContainer>
        <Text className="InvoicePage-card-heading" textStyle="heading-sm">
          Payment history
        </Text>
        <Box css={{ margin: 'xs' }} />
        <Table.Responsive>
          <Table.Root
            appearance="horizontal"
            aria-label="Payment history table"
            alignments={Array<'center'>(columnCount).fill('center')}
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Payment date</Table.HeaderCell>
                {paymentsEnabled && <Table.HeaderCell>Payment method</Table.HeaderCell>}
                <Table.HeaderCell>Amount paid</Table.HeaderCell>
                <Table.HeaderCell>Payment number</Table.HeaderCell>
                <Table.HeaderCell>Transaction status</Table.HeaderCell>
                {paymentsEnabled && <Table.HeaderCell>Paid by</Table.HeaderCell>}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sortedPayments.map((payment) => (
                <Table.Row key={payment.paymentNumber}>
                  <Table.Cell>{formatDate(payment.createdTime)}</Table.Cell>
                  {paymentsEnabled && <Table.Cell>{billingUtils.formatPaymentMethod(payment)}</Table.Cell>}
                  <Table.Cell>{numberUtils.asCurrency(payment.appliedPaymentAmount)}</Table.Cell>
                  <Table.Cell>{payment.paymentNumber}</Table.Cell>
                  <Table.Cell>
                    <InvoicePaymentDisplay value={payment} />
                  </Table.Cell>
                  {paymentsEnabled && <Table.Cell>{payment.email || '-'}</Table.Cell>}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Table.Responsive>
        <Box css={{ paddingBottom: 'xs' }} />
      </StyledContainer>
    );
  };

  const renderInvoiceDetails = () => {
    if (!currentInvoice) return;
    return (
      <Box className="InvoicePage-container">
        <StyledContainer className="InvoicePage-card">
          <Text className="InvoicePage-card-heading" textStyle="heading-sm">
            Account summary
          </Text>
          <Box css={{ display: 'flex', flexWrap: 'wrap' }}>
            <Box css={{ width: { base: '100%', lg: '33.33%' } }}>
              <StyledSummaryBox>
                <Text textStyle="body-bold" asChild>
                  <h6>Current invoice amount</h6>
                </Text>
                <Text textStyle="heading-sm">{numberUtils.asCurrency(currentInvoice.amount)}</Text>
              </StyledSummaryBox>
            </Box>
            <Box css={{ width: { base: '100%', lg: '33.33%' } }}>
              <StyledSummaryBox>
                <Text textStyle="body-bold" asChild>
                  <h6>Current balance</h6>
                </Text>
                <Text textStyle="heading-sm">{numberUtils.asCurrency(currentInvoice.balance)}</Text>
              </StyledSummaryBox>
            </Box>
            <Box css={{ width: { base: '100%', lg: '33.33%' } }}>
              <StyledSummaryBox>
                <Text textStyle="body-bold" asChild>
                  <h6>Total account balance</h6>
                </Text>
                <Text textStyle="heading-sm">
                  {numberUtils.asCurrencyWithDefault(currentInvoice.totalAccountBalance, '-')}
                </Text>
              </StyledSummaryBox>
            </Box>
          </Box>
        </StyledContainer>
        {renderPaymentHistory()}
        {currentInvoice.zuoraInvoiceItems?.length > 0 && <InvoiceItemDetailsTable currentInvoice={currentInvoice} />}
        {currentInvoice.invoiceType !== ZuoraInvoiceType.MULTIFAMILY && currentInvoice?.sfhLineItems.length > 0 && (
          <SfhBreakdownTable currentInvoice={currentInvoice} />
        )}
        {currentInvoice.invoiceType !== ZuoraInvoiceType.MULTIFAMILY && currentInvoice?.sfhLineItems.length > 0 && (
          <ListingSpotlightTable currentInvoice={currentInvoice} />
        )}
      </Box>
    );
  };

  const handleDownloadClick = () => {
    const invoiceNumbers = [];
    invoiceNumbers.push(invoiceId);
    getInvoices(invoiceNumbers);
  };

  const handleDownloadCsv = () => {
    if (!currentInvoice) return;
    if (isSingleFamilyLegacy) {
      downloadSingleFamilyLegacyCsv(currentInvoice, user);
    } else {
      downloadRocketshipCsv(currentInvoice, user);
    }
  };

  const handlePayInvoiceClick = (invoiceId: string) => {
    history.push(`${paths.billingPage}/${paths.invoicePage}/${invoiceId}/payment`);
  };

  const canDownloadCsv =
    currentInvoice &&
    (currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_DAILY_LISTINGS ||
      currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_REIT ||
      currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_FLAT_FEE) &&
    currentInvoice?.sfhLineItems.length > 0;

  const renderHeader = () => {
    if (!currentInvoice) return;
    return (
      <>
        <div>
          <Text className="InvoicePage-heading" textStyle="heading-sm">
            {currentInvoice.accountName}
          </Text>
          <Box css={{ marginLeft: 'sm' }} asChild>
            <span>
              <TextButton
                onClick={handleDownloadClick}
                disabled={isMaintenanceMode}
                data-eventid={EVENT_IDS.DOWNLOAD_INVOICES}
              >
                Download PDF
              </TextButton>
            </span>
          </Box>
          {canDownloadCsv && (
            <Box css={{ marginLeft: 'sm' }} asChild>
              <span>
                <TextButton onClick={handleDownloadCsv} data-eventid={EVENT_IDS.DOWNLOAD_CSV}>
                  Download CSV
                </TextButton>
              </span>
            </Box>
          )}
        </div>
        <Box css={{ display: 'flex', flexWrap: 'wrap' }}>
          <Box css={{ width: { base: '100%', md: '50%' } }}>
            <Box css={{ paddingBottom: 'sm' }}>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text textStyle="body-bold">Invoice number</Text>
                <Text>{invoiceId}</Text>
              </Box>
            </Box>
            <Box css={{ paddingBottom: 'sm' }}>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text textStyle="body-bold">Invoice date</Text>
                <Text>
                  {currentInvoice.invoiceLocalDate
                    ? format(localDateToDate(currentInvoice.invoiceLocalDate), 'MM/dd/yyyy')
                    : '-'}
                </Text>
              </Box>
            </Box>
            <Box css={{ paddingBottom: 'sm' }}>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text textStyle="body-bold">Service period</Text>
                <Text>{billingUtils.calculateServicePeriod(currentInvoice.zuoraInvoiceItems)}</Text>
              </Box>
            </Box>
            <Box css={{ paddingBottom: 'sm' }}>
              <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text textStyle="body-bold">Last updated</Text>
                <Text>{billingUtils.formatZuoraRefresh(currentInvoice.lastTimeSyncedMs)}</Text>
              </Box>
            </Box>
          </Box>
          {currentInvoice.balance > 0 && !paymentsEnabled && (
            <StyledBalanceBox css={{ width: { base: '100%', md: '50%' } }}>
              <Text asChild>
                <p>{`Balance due by ${localDateToDate(currentInvoice.invoiceDueDate).toLocaleDateString()}`}</p>
              </Text>
              <Text className="InvoicePage-amount-due" asChild>
                <p>{numberUtils.asCurrency(currentInvoice.balance)}</p>
              </Text>
              <div onClick={handlePayInfo} className="Utils-primary Invoice-Page-how-to-pay">
                How to pay?
              </div>
            </StyledBalanceBox>
          )}
          {paymentsEnabled && (
            <StyledBalanceBox css={{ width: { base: '100%', md: '50%' } }}>
              {currentInvoice.balance > 0 && (
                <Text asChild>
                  <p>
                    {currentInvoice.accountAutopay
                      ? `Next payment date: ${billingUtils.getNextAutoPaymentDate()}`
                      : `Balance due by ${localDateToDate(currentInvoice.invoiceDueDate).toLocaleDateString()}`}
                  </p>
                </Text>
              )}
              <Text asChild>
                <p>
                  Auto Pay:
                  <b>{currentInvoice.accountAutopay ? 'ON' : 'OFF'}</b>{' '}
                  {currentInvoice.canMakePayments && (
                    <TextButton
                      disabled={isMaintenanceMode}
                      onClick={() => handleEditClick(currentInvoice.accountNumber)}
                    >
                      Edit
                    </TextButton>
                  )}
                </p>
              </Text>
              {currentInvoice.balance > 0 && (
                <Text className="InvoicePage-amount-due" asChild>
                  <p>{numberUtils.asCurrency(currentInvoice.balance)}</p>
                </Text>
              )}
              {currentInvoice.canMakePayments && currentInvoice.balance > 0 && (
                <>
                  <Box css={{ marginTop: 'sm' }} />
                  <Button
                    tone="brand"
                    emphasis="filled"
                    onClick={() => handlePayInvoiceClick(currentInvoice.invoiceNumber)}
                    disabled={isMaintenanceMode}
                  >
                    Make a payment
                  </Button>
                </>
              )}
            </StyledBalanceBox>
          )}
        </Box>
      </>
    );
  };

  const renderLoadingHeader = () => (
    <>
      <Box css={{ display: 'flex', flexWrap: 'wrap' }}>
        <Box css={{ width: '66.67%' }}>
          <Text className="InvoicePage-heading" textStyle="heading-sm">
            {invoiceId}
          </Text>
        </Box>
      </Box>
      {currentInvoiceLoadingState !== LoadingConstants.ERROR && (
        <Box css={{ display: 'flex', flexWrap: 'wrap' }}>
          <Box css={{ flex: 1 }}>
            <SpinnerContainer />
          </Box>
        </Box>
      )}
    </>
  );

  const isLoaded = currentInvoiceLoadingState === LoadingConstants.SUCCESS;

  return (
    <AuthCheckWrapper>
      <div
        className="InvoicePage"
        data-eventid={EVENT_IDS.VIEW_RENTALS_BILLING_INVOICE}
        data-eventdescription="View billing invoice page event"
      >
        <HeadWrapper title={DEFAULT_PAGE_TITLE} />
        <PageWrapper
          title=""
          backButton={
            <TextButton asChild icon={<IconChevronLeftFilled />}>
              <Link to={queryParams.prev === 'paid' ? '/rental-manager/billing/paid' : '/rental-manager/billing/due'}>
                Back
              </Link>
            </TextButton>
          }
        >
          <BillingPortalMaintenanceBanner />
          <StyledContainer className="InvoicePage-info-row">
            {isLoaded ? renderHeader() : renderLoadingHeader()}
          </StyledContainer>
          {currentInvoiceErrorMessage && <BillingError errorMsg={currentInvoiceErrorMessage} />}
          {isLoaded && renderInvoiceDetails()}
        </PageWrapper>
        {showPaymentInfoModal && <PaymentInfoModal onClose={() => setShowPaymentInfoModal(false)} />}
      </div>
    </AuthCheckWrapper>
  );
};

export default withErrorBoundary(InvoicePage);
